import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { PageLoadingFallback } from '@/components/PageLoadingFallback';
import { RETAIL_MODE } from '@/lib/retailMode';

/**
 * Retail-mode admin gate. In single-seller mode only the owner (admin) gets
 * the full app shell. Any non-admin signed-in user is silently signed out and
 * sent back to the public storefront — buyer accounts don't exist in this
 * mode.
 *
 * When `RETAIL_MODE` is false this becomes a passthrough.
 */
export function NonAdminGate({ children }: { children: React.ReactNode }) {
	const { user, loading } = useAuth();
	const { isAdmin, isLoading: roleLoading } = useIsAdmin();

	useEffect(() => {
		if (!RETAIL_MODE) return;
		if (loading || roleLoading) return;
		if (user && !isAdmin) {
			// Non-admin slipped in — sign out and bounce.
			supabase.auth.signOut();
		}
	}, [user, isAdmin, loading, roleLoading]);

	if (!RETAIL_MODE) return <>{children}</>;
	if (loading || roleLoading) return <PageLoadingFallback />;
	if (user && !isAdmin) return <Navigate to="/" replace />;
	return <>{children}</>;
}
